import React, { useEffect, useState } from 'react';

import { Toolbar } from '@mui/material';
import { MAIN_THEME } from '@styles';

import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';

interface IListingToolbar {
  onChange: (value: string) => void;
  cdnUrl: string;
  notes?: boolean;
  title?: string;
  separator?: boolean;
  filterElem?: JSX.Element;
  classes?: { leftSection?: string; rightSection?: string };
  forceClearSearchValue?: boolean;
  hideSearch?: boolean;
  fieldNextToTitle?: JSX.Element;
}

const useStyles = makeStyles<{
  cdnUrl: string;
  separator: boolean;
  fieldNextToTitle?: JSX.Element;
}>()((theme, { separator, cdnUrl, fieldNextToTitle }) => ({
  root: {
    padding: '1px 16px',
    minHeight: '50px!important',
    flexWrap: 'wrap',
    justifyContent: 'space-between'
  },
  searchbar: {
    color: MAIN_THEME.color.black,
    height: '23px',
    minWidth: '150px',
    width: '50%',
    border: 'unset',
    outline: 'unset',
    backgroundColor: 'transparent',
    fontFamily: MAIN_THEME.font.regular,
    '&:focus': {
      '-webkit-input-placeholder': { color: 'transparent' }
    },
    marginRight: !separator ? 'calc(2em + 10px)' : '0'
  },
  titleWrapper: {
    marginTop: fieldNextToTitle ? '-29px' : '3px',
    marginBottom: fieldNextToTitle ? '-29px' : '3px',
    flexGrow: 1,
    flexShrink: 1,
    minWidth: '30%',
    display: 'flex',
    alignItems: fieldNextToTitle ? 'center' : ''
  },
  title: {
    flexGrow: 1,
    flexShrink: 1,
    fontFamily: MAIN_THEME.font.bold,
    fontSize: MAIN_THEME.fontSize.rg,
    color: MAIN_THEME.color.black,
    alignSelf: 'center'
  },
  fieldNextToTitle: {
    marginLeft: '10px',
    display: 'flex',
    alignItems: 'center'
  },
  separator: {
    width: '1px',
    height: '25px',
    opacity: 0.5,
    backgroundColor: MAIN_THEME.color.white86,
    marginLeft: 'calc(1em + 5px)',
    marginRight: 'calc(1em + 5px)',
    display: 'inline-block',
    verticalAlign: 'text-bottom',
    alignSelf: 'center'
  },
  filterContainer: {
    display: 'flex',
    paddingLeft: '40px',
    background: `url(${cdnUrl}/icons/filter-thick-primary-new.svg)`,
    backgroundSize: '16px 16px',
    backgroundPosition: 'top 4px left 0',
    backgroundRepeat: 'no-repeat',
    boxSizing: 'border-box'
  }
}));

const ListingToolbar: React.FC<React.PropsWithChildren<IListingToolbar>> = ({
  onChange,
  cdnUrl,
  classes = {},
  filterElem,
  children,
  title,
  separator = false,
  forceClearSearchValue = false,
  hideSearch = false,
  fieldNextToTitle
}) => {
  const { classes: toolbarClasses } = useStyles({
    cdnUrl,
    separator,
    fieldNextToTitle
  });
  const [searchValue, setSearchValue] = useState<string>('');

  useEffect(() => {
    if (forceClearSearchValue) {
      setSearchValue('');
    }
  }, [forceClearSearchValue]);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    onChange(event.target.value ? event.target.value.trim() : '');
  };

  const renderFilterInput = () => (
    <>
      {!hideSearch && (
        <div
          className={toolbarClasses.filterContainer}
          style={{ width: filterElem ? 'max-content' : 'min-content' }}
        >
          {filterElem ? (
            <>
              {filterElem}
              <span className={toolbarClasses.separator} />
            </>
          ) : null}
          <input
            type="text"
            className={toolbarClasses.searchbar}
            placeholder={'Search'}
            onChange={handleChange}
            onFocus={(e) => {
              e.target.placeholder = '';
            }}
            onBlur={(e) => {
              e.target.placeholder = 'Search';
            }}
            value={searchValue}
          />
        </div>
      )}
    </>
  );

  return (
    <Toolbar className={toolbarClasses.root}>
      <div className={clsx(toolbarClasses.titleWrapper, classes.leftSection)}>
        {title && (
          <>
            <div className={toolbarClasses.title}>{title}</div>
            {fieldNextToTitle && (
              <div className={toolbarClasses.fieldNextToTitle}>
                {fieldNextToTitle}
              </div>
            )}
          </>
        )}
        {renderFilterInput()}
      </div>
      <div className={classes.rightSection} style={{ display: 'flex' }}>
        {separator && <span className={toolbarClasses.separator} />}
        {children}
      </div>
    </Toolbar>
  );
};

export default ListingToolbar;
